import React from "react";
import { FiCheckCircle, FiShield, FiCalendar, FiMessageSquare, FiTrendingUp, FiSmartphone, FiAward, FiBookOpen } from "react-icons/fi";

export const LandingAbout: React.FC = () => {
  const features = [
    {
      icon: <FiSmartphone className="text-blue-600" size={26} />,
      title: "Real-Time Childcare App",
      description: "A robust mobile & web app that enables parents to stay updated on daily school activities, student attendance, and classroom performance.",
      badge: "Parent Engagement",
      bgColor: "bg-blue-50/80",
      borderColor: "border-blue-100",
    },
    {
      icon: <FiShield className="text-emerald-600" size={26} />,
      title: "Secured Pickup Authorization",
      description: "Ensure maximum student safety with an authorized pickup portal featuring dynamic security passcodes and verified guardian verification.",
      badge: "Student Safety",
      bgColor: "bg-emerald-50/80",
      borderColor: "border-emerald-100",
    },
    {
      icon: <FiBookOpen className="text-indigo-600" size={26} />,
      title: "Gamified Learning & Curriculum",
      description: "Digital tools that simplify instructional alignment with learning outcomes using interactive gamified study resources and digital result processing.",
      badge: "Academic Growth",
      bgColor: "bg-indigo-50/80",
      borderColor: "border-indigo-100",
    },
    {
      icon: <FiMessageSquare className="text-purple-600" size={26} />,
      title: "Instant Parent-School Portal",
      description: "Seamless communication portal with direct messaging, instant notifications, fee invoicing, and broadcast announcements.",
      badge: "Communication",
      bgColor: "bg-purple-50/80",
      borderColor: "border-purple-100",
    },
    {
      icon: <FiCalendar className="text-amber-600" size={26} />,
      title: "Virtual School Event Calendar",
      description: "An integrated interactive calendar that improves parental engagement for upcoming school term activities, exams, and celebrations.",
      badge: "Event Tracking",
      bgColor: "bg-amber-50/80",
      borderColor: "border-amber-100",
    },
    {
      icon: <FiTrendingUp className="text-teal-600" size={26} />,
      title: "Automated Fee Billing & Verifications",
      description: "Issue term invoices, process student fee settlements, verify proof of payment receipts, and manage outstanding balances effortless.",
      badge: "Smart Billing",
      bgColor: "bg-teal-50/80",
      borderColor: "border-teal-100",
    },
  ];

  return (
    <div id="about" className="py-20 bg-slate-50 relative overflow-hidden">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* Mission Statement Banner */}
        <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 rounded-[2.5rem] p-8 sm:p-12 shadow-2xl text-white relative overflow-hidden" data-aos="fade-up">
          
          <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-black uppercase tracking-widest border border-blue-400/20">
              Our Purpose & Mission
            </span>

            <h2 className="text-2xl sm:text-4xl font-black leading-tight tracking-tight">
              Reshaping the Future of Education Across Africa
            </h2>

            <p className="text-sm sm:text-lg text-slate-300 font-medium leading-relaxed">
              <strong className="text-white font-bold">Pendulum Digital</strong> is dedicated to offering a multifaceted digital solution that makes practical strategy and alignment with learning seamless for educators and pupils, while equipping parents with real-time transparent information.
            </p>

            <div className="pt-2 flex flex-wrap justify-center gap-4 text-xs font-bold text-blue-200">
              <span className="flex items-center gap-1.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md">
                <FiCheckCircle className="text-emerald-400" /> Educator Empowerment
              </span>
              <span className="flex items-center gap-1.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md">
                <FiCheckCircle className="text-emerald-400" /> Transparent Parental Engagement
              </span>
              <span className="flex items-center gap-1.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md">
                <FiCheckCircle className="text-emerald-400" /> Cost & Time Efficiency
              </span>
            </div>
          </div>
        </div>

        {/* How Pendulum Works Header */}
        <div id="how-it-works" className="text-center space-y-4 max-w-3xl mx-auto" data-aos="fade-up">
          <span className="text-xs font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full border border-blue-100">
            Core Modules & Features
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
            How Pendulum Powers Modern Schools
          </h2>
          <p className="text-sm sm:text-base text-slate-600 font-medium">
            Everything your school needs to automate administrative tasks, boost student performance, and keep parents informed in one unified platform.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((item, idx) => (
            <div
              key={idx}
              data-aos="fade-up"
              data-aos-delay={idx * 100}
              className={`bg-white rounded-[2rem] p-8 border ${item.borderColor} shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between group`}
            >
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className={`w-14 h-14 rounded-2xl ${item.bgColor} flex items-center justify-center border ${item.borderColor} group-hover:scale-110 transition-transform`}>
                    {item.icon}
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                    {item.badge}
                  </span>
                </div>

                <h3 className="text-xl font-black text-slate-900 group-hover:text-blue-600 transition-colors">
                  {item.title}
                </h3>

                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal">
                  {item.description}
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-slate-100 flex items-center text-xs font-bold text-blue-600 group-hover:translate-x-1 transition-transform">
                Learn more module details →
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default LandingAbout;
